import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquare, Send, Loader2, Users, PlusCircle } from "lucide-react";
import FileUploader, { FileTile } from "@/components/FileUploader";
import { useAuth } from "@/context/AuthContext";

/**
 * Reusable messages inbox. Used inside Instructor and Alumno layouts.
 * Polls /messages/threads every 15 s.
 */
export default function MessagesInbox({ role = "instructor" }) {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [sending, setSending] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const listEnd = useRef(null);

  const loadThreads = async () => {
    try {
      const r = await api.get("/messages/threads");
      setThreads(r.data.items || []);
    } catch (e) {
      // silent — poll again
    }
  };
  const loadThread = async (thread) => {
    setActive(thread);
    try {
      const r = await api.get(`/messages/threads/${thread.thread_id}`);
      setMessages(r.data.messages || []);
      setTimeout(() => listEnd.current?.scrollIntoView({ behavior: "smooth" }), 40);
      loadThreads(); // refresh unread badges
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  useEffect(() => {
    loadThreads();
    api.get("/messages/contacts").then((r) => setContacts(r.data.items || [])).catch(() => {});
    const id = setInterval(() => {
      loadThreads();
      if (active) {
        api.get(`/messages/threads/${active.thread_id}`)
          .then((r) => setMessages(r.data.messages || []))
          .catch(() => {});
      }
    }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.thread_id]);

  const send = async (e) => {
    e.preventDefault();
    if (!active || (!body.trim() && attachments.length === 0)) return;
    setSending(true);
    try {
      await api.post("/messages/send", {
        to_user_id: active.peer_user_id,
        body: body.trim(),
        attachments,
      });
      setBody("");
      setAttachments([]);
      const r = await api.get(`/messages/threads/${active.thread_id}`);
      setMessages(r.data.messages || []);
      setTimeout(() => listEnd.current?.scrollIntoView({ behavior: "smooth" }), 40);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const startNewChat = async (peer) => {
    // Optimistic thread; server-side deterministic thread_id makes it show up after first send
    const tempThread = {
      thread_id: `pending-${peer.id}`,
      peer_user_id: peer.id, peer_name: peer.name, peer_roles: peer.roles,
      last_body: "", last_at: null, unread_count: 0,
    };
    setActive(tempThread);
    setMessages([]);
    setShowNew(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 h-[calc(100vh-180px)] min-h-[500px]">
      <aside className="rounded-2xl border border-white/10 bg-white/5 overflow-y-auto">
        <div className="p-3 flex items-center justify-between border-b border-white/10">
          <p className="text-xs uppercase tracking-widest font-semibold text-gray-400 inline-flex items-center gap-1">
            <MessageSquare className="w-3 h-3" /> Conversaciones
          </p>
          <button onClick={() => setShowNew((s) => !s)} data-testid="msg-new-chat-btn"
                  className="p-1 rounded text-gray-300 hover:bg-white/10">
            <PlusCircle className="w-4 h-4" />
          </button>
        </div>
        {showNew && (
          <div className="border-b border-white/10 p-2 bg-black/20">
            <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2 inline-flex items-center gap-1">
              <Users className="w-3 h-3" /> {role === "alumno" ? "Instructores disponibles" : "Contactos"}
            </p>
            {contacts.length === 0 ? (
              <p className="text-xs text-gray-500 px-1">Sin contactos por ahora.</p>
            ) : (
              contacts.map(c => (
                <button key={c.id} onClick={() => startNewChat(c)}
                        data-testid={`msg-contact-${c.id}`}
                        className="w-full text-left px-2 py-2 rounded hover:bg-white/10 text-sm text-white">
                  {c.name}
                  <span className="ml-1 text-[10px] text-gray-500">· {c.roles?.[0] || ""}</span>
                </button>
              ))
            )}
          </div>
        )}
        {threads.length === 0 && !showNew ? (
          <div className="p-6 text-center text-xs text-gray-500">Sin conversaciones aún.</div>
        ) : (
          threads.map(t => (
            <button key={t.thread_id} onClick={() => loadThread(t)}
                    data-testid={`msg-thread-${t.thread_id}`}
                    className={`w-full text-left p-3 border-b border-white/5 hover:bg-white/[0.03] ${active?.thread_id === t.thread_id ? "bg-white/[0.06]" : ""}`}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-white truncate">{t.peer_name}</p>
                {t.unread_count > 0 && (
                  <span className="text-[10px] bg-[#F26A21] text-[#0B0F14] font-bold px-1.5 rounded" data-testid="msg-thread-unread">
                    {t.unread_count}
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-xs text-gray-400 truncate">{t.last_from_me ? "Tú: " : ""}{t.last_body || (t.has_attachments ? "📎 Archivo" : "")}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">{t.last_at ? new Date(t.last_at).toLocaleString("es-MX") : ""}</p>
            </button>
          ))
        )}
      </aside>

      <section className="rounded-2xl border border-white/10 bg-white/5 flex flex-col overflow-hidden">
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-sm text-gray-500 text-center p-8">
            Selecciona una conversación o inicia una nueva.
          </div>
        ) : (
          <>
            <div className="border-b border-white/10 px-4 py-3">
              <p className="text-xs uppercase tracking-widest text-gray-500">Conversación con</p>
              <p className="text-sm font-bold text-white">{active.peer_name}</p>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="msg-thread-body">
              {messages.length === 0 ? (
                <p className="text-xs text-gray-500 text-center">Aún no hay mensajes. Envía el primero.</p>
              ) : (
                messages.map(m => {
                  const mine = m.from_user_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${mine ? "bg-[#F26A21] text-[#0B0F14]" : "bg-white/10 text-white"}`}>
                        {!mine && <p className="text-[10px] uppercase tracking-widest font-bold opacity-70">{m.from_user_name}</p>}
                        {m.body && <p className="text-sm whitespace-pre-wrap">{m.body}</p>}
                        {m.attachments?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {m.attachments.map(a => (
                              <a key={a.file_id || a.id} target="_blank" rel="noreferrer"
                                 href={`${BACKEND_URL}${a.url || `/api/files/${a.storage_path}`}`}
                                 className={`block text-xs underline ${mine ? "text-[#0B0F14]/80" : "text-[#F26A21]"}`}>
                                📎 {a.filename}
                              </a>
                            ))}
                          </div>
                        )}
                        <p className={`mt-1 text-[10px] ${mine ? "text-[#0B0F14]/60" : "text-gray-400"}`}>
                          {new Date(m.created_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={listEnd} />
            </div>
            <form onSubmit={send} className="border-t border-white/10 p-3 space-y-2" data-testid="msg-send-form">
              {attachments.length > 0 && (
                <div className="grid gap-2">
                  {attachments.map(a => (
                    <FileTile key={a.id} file={a} onRemove={(f) => setAttachments(p => p.filter(x => x.id !== f.id))} />
                  ))}
                </div>
              )}
              <div className="flex items-end gap-2">
                <textarea value={body} onChange={(e) => setBody(e.target.value)}
                          rows={1}
                          data-testid="msg-body-input"
                          placeholder="Escribe un mensaje…"
                          className="flex-1 rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white resize-none" />
                <FileUploader kind="message" resourceType="message" compact multiple={false}
                              onUploaded={(f) => setAttachments(p => [...p, f])}
                              buttonLabel="📎" hint={null} />
                <button type="submit" disabled={sending || (!body.trim() && attachments.length === 0)}
                        data-testid="msg-send-btn"
                        className="px-3 py-2 rounded-md bg-[#F26A21] text-[#0B0F14] font-semibold text-sm disabled:opacity-50 inline-flex items-center gap-1">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </form>
          </>
        )}
      </section>
    </div>
  );
}
